import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useSelector, useDispatch } from 'react-redux';
import { useLanguage, getPaymentsPageTranslation } from '../hooks/useLanguage';
import { fetchCreditStats } from '../store/reducers/index';
import { CardStat } from '../components/CardStat';

const API_BASE_URL = process.env.REACT_APP_DOCS_API_KEY;

const getCurrentUserId = () => {
  const userId = sessionStorage.getItem('user_id');
  if (userId) return userId;
  const userIdFromCookie = Cookies.get('userId');
  if (userIdFromCookie) return userIdFromCookie;
  return null;
};

const sortOptions = [
  { value: 'date-desc', labelKey: 'newestFirst' },
  { value: 'date-asc', labelKey: 'oldestFirst' },
  { value: 'amount-desc', labelKey: 'amountHighLow' },
  { value: 'amount-asc', labelKey: 'amountLowHigh' },
];


const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isSpanish] = useLanguage();
  const products = useSelector(state => state.products);
  const dispatch = useDispatch();
  const creditStats = useSelector(state => state.creditStats);

  useEffect(() => {
    dispatch(fetchCreditStats());
  }, [dispatch]);

  useEffect(() => {
    const userId = getCurrentUserId();
    if (!userId) {
      setError(getPaymentsPageTranslation('userNotLoggedIn', isSpanish));
      setLoading(false);
      return;
    }
    const fetchPayments = async () => {
      try {
        const response = await fetch(`https://${API_BASE_URL}/api/payments/user?user_id=${userId}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || getPaymentsPageTranslation('failedToFetchPayments', isSpanish));
        setPayments(Array.isArray(data) ? data : []);
        console.log('Fetched payments:', data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPayments();
  }, [isSpanish]);

  const filteredPayments = payments.filter(p =>
    (p.product_id || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedPayments = [...filteredPayments].sort((a, b) => {
    switch (sortBy) {
      case 'date-asc':
        return new Date(a.date_created || a.created_at) - new Date(b.date_created || b.created_at);
      case 'date-desc':
        return new Date(b.date_created || b.created_at) - new Date(a.date_created || a.created_at);
      case 'amount-asc':
        return (a.amount || 0) - (b.amount || 0);
      case 'amount-desc':
        return (b.amount || 0) - (a.amount || 0);
      default:
        return 0;
    }
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    const pad = (n) => n.toString().padStart(2, '0');
    let hours = d.getHours();
    const minutes = pad(d.getMinutes());
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}, ${pad(hours)}:${minutes} ${ampm}`;
  };

  const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

  return (
    <div className="text-left">
      <h1 className='text-3xl font-bold mb-20'>{getPaymentsPageTranslation('myCreditHistory', isSpanish)}</h1>
      {/* Card Section */}
      <div className="grid grid-cols-3 gap-4 mb-8 pb-8 border-bottom-gray">
        <CardStat value={Math.max(0, creditStats.current?.remainingCredits ?? 0)} title={isSpanish ? 'Créditos restantes' : 'Remaining Credits'} />
        <CardStat value={Math.max(0, creditStats.usage?.spentToday ?? 0)} title={isSpanish ? 'Usados hoy' : 'Used Today'} />
        <CardStat value={Math.max(0, creditStats.usage?.spentThisMonth ?? 0)} title={isSpanish ? 'Usados este mes' : 'Used This Month'}/>
      </div>
      {/* Header and controls */}
      <div className='flex justify-between gap-4 mb-4 items-center'>
        <h2 className='text-xl font-bold'>{isSpanish ? 'Historial de créditos' : 'Credit History'}</h2>
        <div className="flex gap-2 items-center">
          {/* Custom Sort Dropdown */}
          <div className="relative button-no-hover-effect">
            <button
              className="flex items-center gap-2 px-2 py-2 rounded-full truncate"
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <span>{getPaymentsPageTranslation('sort', isSpanish)}</span>
              <svg className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
            </button>
            {isSortOpen && (
              <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortBy === option.value ? 'text-primary' : 'text-gray-700'}`}
                    onClick={() => {
                      setSortBy(option.value);
                      setIsSortOpen(false);
                    }}
                  >
                    {getPaymentsPageTranslation(option.labelKey, isSpanish)}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              className="w-64 pl-4 pr-4 py-2 border-container rounded-full"
              placeholder={getPaymentsPageTranslation('searchCreditHistory', isSpanish)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
        </div>
      </div>
      <table className="min-w-full border-collapse border border-gray-300 mt-4 text-sm">
        <thead>
          <tr>
            {/* Removed Payment ID column */}
            <th className="border border-gray-300 p-2">{getPaymentsPageTranslation('product', isSpanish)}</th>
            <th className="border border-gray-300 p-2">{getPaymentsPageTranslation('amount', isSpanish)}</th>
            <th className="border border-gray-300 p-2">{getPaymentsPageTranslation('currency', isSpanish)}</th>
            <th className="border border-gray-300 p-2">{getPaymentsPageTranslation('status', isSpanish)}</th>
            <th className="border border-gray-300 p-2">{getPaymentsPageTranslation('date', isSpanish)}</th>
          </tr>
        </thead>
        <tbody>
          {sortedPayments.length > 0 ? (
            sortedPayments.map((p) => {
              const product = products.find(prod => prod.productId === p.product_id);
              return (
                <tr key={p.id}>
                  <td className="border border-gray-300 p-2">{product ? `${product.credits} ${getPaymentsPageTranslation('credits', isSpanish)}` : p.product_id || '-'}</td>
                  <td className="border border-gray-300 p-2">{p.amount ? `€${(p.amount / 100).toFixed(2)}` : '-'}</td>
                  <td className="border border-gray-300 p-2">{p.currency ? p.currency.toUpperCase() : ''}</td>
                  <td className="border border-gray-300 p-2">{capitalize(p.status)}</td>
                  <td className="border border-gray-300 p-2">{formatDate(p.date_created || p.created_at)}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="5" className="border border-gray-300 p-2 text-center">
                {getPaymentsPageTranslation('noPaymentsFound', isSpanish)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Payments; 